import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, Form, Input, Modal, Spin } from 'antd';
import { resetPassword } from '@/features/homepage/api/ApiResetPassword';
import { ERR_MESSAGE, REGEX_EMAIL, RESPONSE_ERR } from "@/constant";
import logoCCBJI from "@/assets/img/2800_CCBJI_Logo.bmp";
import logoFVJ from "@/assets/img/2801_FVJ_Logo.bmp";
import storage from '@/utils/storage';
import { LoadingSpinner } from '@/components/SpinnerOverlay';
import NavbarPublic from '@/features/homepage/components/NavbarPublic';

// SCN012

const ResetPasswordPage = () => {
    const [loading, setLoading] = useState(false);
    const [showSystemError, setSystemError] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const inputRef = useRef(null);
    const [form] = Form.useForm();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const titleOpen = <>本登録が完了していません。<br />初回登録へお進みください。</>;

    const navigate = useNavigate();

    useEffect(() => {
        const token = storage.getToken();

        if (token) {
            navigate("/");
        }
        focusID();
    }, [navigate]);

    // Focus to email input field
    const focusID = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }

    // Normalize form input data
    const CheckDataInput = (values: { email?: string; login_id?: string }) => ({
        email: values.email ? values.email.toLowerCase() : null,
        login_id: values.login_id || null,
    });

    // Handle form submission
    const handleSubmit = async (values: any) => {
        const input = CheckDataInput(values);
        if (!input.login_id || !input.email) {
            return;
        }

        setLoading(true);
        setSystemError('');

        try {
            const response = await resetPassword(input);
            setLoading(false);
            if (response?.result == 1) {
                setShowSuccessModal(true);
                navigate("/reset-password-inform", { state: { showSuccessResetPasswordModal: true } });
            } else {
                if (response?.error_code == RESPONSE_ERR.WAS001) {
                    setSystemError(ERR_MESSAGE.WAS001_MESSAGE);
                } else if (response?.error_code == RESPONSE_ERR.WAS002) {
                    setSystemError(ERR_MESSAGE.WAS002_MESSAGE);
                } else if (response?.error_code == RESPONSE_ERR.E0902) {
                    setSystemError(ERR_MESSAGE.WAI002_MESSAGE);
                } else if (response?.error_code == RESPONSE_ERR.WAI006) {
                    setIsModalOpen(true);
                } else {
                    setSystemError(ERR_MESSAGE.WAI003_MESSAGE);
                }

            }
        } catch (error) {
            setLoading(false);
            if (error.response.data === ERR_MESSAGE.WAI001_MESSAGE) {
                setSystemError(ERR_MESSAGE.WAI001_MESSAGE);
            } else if (error.response.data === ERR_MESSAGE.WAI002_MESSAGE) {
                setSystemError(ERR_MESSAGE.WAI002_MESSAGE);
            } else if (error.response.data === ERR_MESSAGE.WAI003_MESSAGE) {
                setSystemError(ERR_MESSAGE.WAI003_MESSAGE);
            } else if (error.response.data === RESPONSE_ERR.E0501) {
                setSystemError(ERR_MESSAGE.WAI003_MESSAGE);
            } else if (error.response.data.error_code == RESPONSE_ERR.WAI006) {
                setIsModalOpen(true);
            } else {
                setSystemError(ERR_MESSAGE.WAI003_MESSAGE);
            }
        } finally {
            setLoading(false);
        }
    };

    // Redirect to login page
    const backToLogin = () => {
        navigate('/login');
    };

    // Show loading spinner while loading
    if (loading == true) {
        return <LoadingSpinner />;
    }

    // Close incomplete registration modal
    const closeModal = () => {
        setIsModalOpen(false);
    }

    return (

        <div style={{ display: loading ? 'none' : 'block' }}>
            <Fragment>
                <NavbarPublic
                    logoCCBJI={logoCCBJI}
                    logoFVJ={logoFVJ}
                    backToLogin={backToLogin}
                />
                <div className="container-fluid background-cover-external">
                    <ol className="breadcrumb">
                        {!showSuccessModal && <li className="breadcrumb-item active">パスワードリセット</li>}
                        {showSuccessModal && <li className="breadcrumb-item active">パスワードリセット完了</li>}
                    </ol>
                    <div className="jumbotron">
                        <div className="section">
                            {!showSuccessModal && (

                                <div className="card-body regular-font" style={{ minHeight: '45vh' }}>
                                    <Form
                                        form={form}
                                        name="reset-password"
                                        layout="vertical"
                                        onFinish={handleSubmit}
                                        size="large"
                                        validateTrigger="onSubmit"
                                    >
                                        <ul className="list-group list-group-flush">
                                            <li className="list-group-item">
                                                <p style={{ color: 'red' }}>{showSystemError}</p>
                                                <span>登録されているメールアドレスを入力してください</span>
                                                <Flex vertical gap={12}>
                                                    <Form.Item
                                                        name="email"

                                                        normalize={value => (value ? value.trim() : '')}
                                                        rules={[
                                                            { required: true, message: "メールアドレスが入力必須" },
                                                            {
                                                                validator: (_, value) => {
                                                                    if (!value) { return Promise.resolve() };

                                                                    const domainRegex = REGEX_EMAIL;
                                                                    const latinRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

                                                                    // eslint-disable-next-line no-control-regex

                                                                    if (!domainRegex.test(value)) {
                                                                        return Promise.reject(ERR_MESSAGE.MESSAGE_ERROR_VALIDATE_EMAIL_REGEX);
                                                                    }
                                                                    if (!latinRegex.test(value)) {
                                                                        return Promise.reject(ERR_MESSAGE.MESSAGE_ERROR_VALIDATE_EMAIL_REGEX);
                                                                    }


                                                                    return Promise.resolve();
                                                                }
                                                            }
                                                        ]}
                                                    >
                                                        <Input ref={inputRef} />
                                                    </Form.Item>

                                                </Flex>
                                            </li>
                                            <li className="list-group-item">
                                                <span>登録されているID（50で始まる番号）を入力してください</span>
                                                <Flex vertical gap={12}>
                                                    <Form.Item
                                                        name="login_id"
                                                        rules={[
                                                            { required: true, message: "IDが入力必須" },
                                                            {
                                                                pattern: /^50[\da-zA-Z]{8}$/,
                                                                message: "IDは50から始まる10桁の数字です"
                                                            },
                                                        ]}
                                                    >
                                                        <Input maxLength={10} />
                                                    </Form.Item>
                                                </Flex>
                                            </li>
                                        </ul>
                                        <ul className="list-group list-group-flush">
                                            <li className="list-group-item">
                                                <span>よろしければ「送信」を押してください</span> <br />
                                                <span>5分経っても通知がこない場合は入力しなおしてください</span> <br />
                                                <span style={{ overflowWrap: 'break-word' }}>配信元 ccbji_touroku@ccbji.co.jp/fvj_touroku@ccbji.co.jp</span> <br />
                                                <br />
                                                <div className="d-flex justify-content-center">
                                                    <Button className="m-2 btn bg-discard col-md-3 col-sm-6" onClick={backToLogin}>
                                                        <span style={{ fontSize: '14px' }} className="text-white fz-14">戻る</span>
                                                    </Button>
                                                    <Button className="m-2 btn bg-coca col-md-3 col-sm-6" htmlType="submit" onClick={handleSubmit}>
                                                        <span style={{ fontSize: '14px' }} className="text-white fz-14">送信 </span>
                                                    </Button>
                                                </div>
                                            </li>
                                        </ul>
                                    </Form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Modal for incomplete registration message */}
                <Modal centered
                    title={titleOpen}
                    open={isModalOpen}
                    closeIcon={false}
                    footer={[
                        <Button key="back"
                            className="notification-modal"
                            onClick={closeModal}>
                            OK
                        </Button>,
                    ]}
                >
                </Modal>
            </Fragment>
        </div >
    );
};

export default ResetPasswordPage;
